import type { CSSProperties } from 'react';
import { FiX, FiArrowRight } from 'react-icons/fi';
import type { EventTicket } from '../../api/events';
import { TicketOptionCard } from './TicketOptionCard';

interface TicketSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  tickets: EventTicket[];
  ticketCounts: Record<number, number>;
  onAdd: (ticketId: number) => void;
  onRemove: (ticketId: number) => void;
  timezone?: string;
  disabled?: boolean;
  onCheckout: () => void;
  checkoutDisabled?: boolean;
  /** Event accent CSS variables — the modal renders outside <main>, so it needs its own copy. */
  accentStyle?: CSSProperties;
}

export function TicketSelectionModal({
  isOpen,
  onClose,
  tickets,
  ticketCounts,
  onAdd,
  onRemove,
  timezone,
  disabled,
  onCheckout,
  checkoutDisabled,
  accentStyle,
}: TicketSelectionModalProps) {
  if (!isOpen) return null;

  const totalTickets = Object.values(ticketCounts).reduce((sum, qty) => sum + qty, 0);
  const isEmpty = totalTickets === 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:px-4 sm:py-4"
      role="dialog"
      aria-modal="true"
      style={accentStyle}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      {/* Panel */}
      <div className="relative w-full sm:max-w-lg min-h-104 max-h-[min(85dvh,42rem)] bg-zinc-950 border border-white/10 rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-white/8 shrink-0">
          <h2 className="text-white text-lg font-semibold">Tickets</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/8 text-zinc-400 hover:text-white transition-colors"
          >
            <FiX size={18} />
          </button>
        </div>

        {/* Ticket list */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {tickets.map((ticket) =>
            ticket.id !== undefined ? (
              <TicketOptionCard
                key={ticket.id}
                ticket={ticket}
                quantity={ticketCounts[ticket.id] ?? 0}
                onAdd={onAdd}
                onRemove={onRemove}
                timezone={timezone}
                disabled={disabled}
              />
            ) : null,
          )}
        </div>

        {/* Checkout */}
        <div className="px-5 py-4 border-t border-white/8 shrink-0">
          <button
            type="button"
            onClick={onCheckout}
            disabled={checkoutDisabled || isEmpty}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-(--event-accent) text-(--event-accent-fg) hover:brightness-110 active:brightness-95 text-[14px] font-semibold shadow-2xl shadow-black/30 ring-1 ring-black/10 transition-[filter] disabled:bg-zinc-700 disabled:text-zinc-400 disabled:shadow-none disabled:ring-0 disabled:cursor-not-allowed"
          >
            Proceed to Checkout
            <FiArrowRight size={15} />
          </button>
        </div>
      </div>
    </div>
  );
}
